import axios from 'axios';

import { getApiBaseUrl } from '../config/ApiConfig';
import { getMidwayJwtToken } from '../auth/MidwayJwtToken';

export interface IgJobRequest {
  business: string;
  region: string;
  asin: string;
  fg: number;
  fcd: string;
  leadtime?: number;
  span?: number;
  nSteps?: number;
  modelS3?: string;
  outputS3?: string;
  // Method selection
  method?: 'ig' | 'perturbation';
  // Perturbation-specific
  mode?: 'group' | 'feature';
  baseline?: 'curated' | 'zero';
  // Perturbation multi-lead-time spec: a single int ("7"), comma list
  // ("0,7,14"), or inclusive range ("0-91"). When present, sent instead of the
  // single numeric `leadtime`; IG requests keep sending `leadtime`.
  leadtimes?: string;
  inputPath?: string;
  entities?: Array<{ fg: string; asin: string }>;
  groups?: string;
  quantiles?: string;
  batchSize?: number;
  numFiles?: number;
  scriptS3?: string;
}

export interface IgSubmitResponse {
  status: string;
  jobId: string;
  jobName: string;
  outputS3: string;
  method?: string;
  mode?: string;
  baseline?: string;
}

export interface IgStatusResponse {
  status: 'submitted' | 'running' | 'succeeded' | 'failed';
  jobId: string;
  jobName: string;
  message?: string;
  error?: string;
  exitCode?: number;
  logStream?: string;
}

export interface IgManifestAsin {
  asin: string;
  fg: number;
  fcd: string;
  // Forecast summary values. IG always populates all of these. The
  // perturbation explainer populates actual_p50/actual_p90 (the unperturbed
  // forecast) when available and omits baseline_*/delta_* (it has no single
  // full-baseline forecast), so all forecast fields are optional here.
  actual_p50?: number;
  actual_p90?: number;
  baseline_p50?: number;
  baseline_p90?: number;
  delta_p50?: number;
  delta_p90?: number;
  status: 'succeeded' | 'failed';
  error?: string;
  output_s3?: string;
  elapsed_s?: number;
}

export interface IgManifest {
  mode: string;
  business?: string | null;
  fg: number;
  fcd: string;
  leadtime: number;
  span: number;
  // IG sets n_steps; perturbation sets it to null and adds method/baseline.
  n_steps?: number | null;
  method?: string;
  baseline?: string;
  vertical?: string | null;
  total_asins: number;
  succeeded: number;
  failed: number;
  wall_time_s: number;
  asins: IgManifestAsin[];
}

export interface IgAttributionRow {
  feature: string;
  /**
   * @deprecated Removed in the scheme-C run_ig output. Older CSVs carried a
   * single p50-based `rank`; new CSVs use `p50_rank`/`p90_rank` instead. Kept
   * optional so pre-v2 results still type-check.
   */
  rank?: number | string;
  day_offset: number | string;
  date: string;
  actual_value: number | string;
  baseline_value: number | string;
  p50_score: number;
  p90_score: number;
  p50_total: number | string;
  p90_total: number | string;
  // v2 schema: per-quantile top-N rank for the day (1..N), '' if not in that
  // quantile's top-N. Absent entirely on pre-v2 CSVs.
  p50_rank?: number | string;
  p90_rank?: number | string;
  // Perturbation multi-lead-time: the lead time (in days) this row's scores
  // were computed for. A multi-lead-time perturbation CSV stacks one row per
  // (feature x lead_time x quantile); IG CSVs do not carry this column.
  lead_time?: number;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await getMidwayJwtToken();
  return { Authorization: `Bearer ${token}` };
}

export async function submitIgJob(
  request: IgJobRequest,
  signal?: AbortSignal
): Promise<IgSubmitResponse> {
  const response = await axios.post<IgSubmitResponse>(
    `${getApiBaseUrl()}/ig/submit`,
    request,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

export async function getIgJobStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<IgStatusResponse> {
  const response = await axios.get<IgStatusResponse>(
    `${getApiBaseUrl()}/ig/status/${encodeURIComponent(jobId)}`,
    { headers: await authHeader(), signal }
  );
  return response.data;
}

export async function fetchIgResults(
  outputS3: string,
  signal?: AbortSignal
): Promise<IgAttributionRow[]> {
  const response = await axios.get<IgAttributionRow[]>(
    `${getApiBaseUrl()}/ig/results`,
    {
      params: { outputS3 },
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

export async function fetchIgManifest(
  outputS3: string,
  signal?: AbortSignal
): Promise<IgManifest> {
  const response = await axios.get<IgManifest>(
    `${getApiBaseUrl()}/ig/results`,
    {
      params: { outputS3, file: 'manifest' },
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

export async function fetchIgAsinResults(
  asinOutputS3: string,
  signal?: AbortSignal
): Promise<IgAttributionRow[]> {
  const response = await axios.get<IgAttributionRow[]>(
    `${getApiBaseUrl()}/ig/results`,
    {
      params: { outputS3: asinOutputS3 },
      headers: await authHeader(),
      signal,
    }
  );
  return response.data;
}

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes (Batch jobs are slow)

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function runIgJob(
  request: IgJobRequest,
  onStatusChange?: (status: string) => void,
  signal?: AbortSignal
): Promise<{ outputS3: string; jobId: string }> {
  const submitResponse = await submitIgJob(request, signal);
  const { jobId, outputS3 } = submitResponse;

  onStatusChange?.('submitted');

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException('Job cancelled', 'AbortError');
    }
    await delay(POLL_INTERVAL_MS);

    const status = await getIgJobStatus(jobId, signal);
    onStatusChange?.(status.status);

    if (status.status === 'succeeded') {
      return { outputS3, jobId };
    }
    if (status.status === 'failed') {
      throw new Error(status.error || `Batch job failed (exit code: ${status.exitCode})`);
    }
  }

  throw new Error('IG job timed out after 30 minutes');
}
